import { useEffect, useId, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link2 } from 'lucide-react'
import { useForm, useWatch, Controller, type Resolver } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { createRecurringFromTransaction } from '@/api/recurring'
import {
  fetchSpendMonthSuggestion,
  suggestTransactions,
  type CreateTransactionPayload,
  type LinkedTransactionOption,
  type TransactionSuggestion,
  type TransactionSummary,
} from '@/api/transactions'
import type { AccountSummary } from '@/api/accounts'
import type { Category } from '@/api/categories'
import { listMembers } from '@/api/members'
import { createPaidForOthers, listContacts } from '@/api/people'
import { AmountCalculatorInput } from '@/components/AmountCalculatorInput'
import { DatePicker } from '@/components/DatePicker'
import { AddPersonInline } from '@/components/owed/AddPersonInline'
import { SplitSection, splitPayload, type SplitValue } from '@/components/owed/SplitSection'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { MENU_SURFACE } from '@/components/ui/menu-surface'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { useFormatCurrency } from '@/hooks/useFormatCurrency'
import { track } from '@/lib/analytics'
import { SHARED_EXPENSES_ENABLED } from '@/lib/featureFlags'
import { fillRemainder, partsError, toPayload, type PartRow } from '@/lib/paymentParts'
import { splitError, toPaise, yourShare } from '@/lib/splitMath'
import { formatDate } from '@/lib/formatDate'
import { addMonths, monthOf } from '@/lib/spendMonth'
import { today, toMemberOptions } from '@/lib/transactionDateRangeFilter'
import { kindInfo, type AddKind } from './addKinds'
import { PaymentSourcesSection } from './PaymentSourcesSection'
import { SpendMonthField } from './SpendMonthField'
import {
  REPEATS_OFF,
  RepeatsSection,
  effectiveNextDue,
  repeatsError,
  type RepeatsValue,
} from './RepeatsSection'
import {
  AmountHero,
  CategoryListbox,
  Field,
  FormShell,
  InfoNote,
  MoreSection,
  TransactionLinkCombobox,
} from './fields'
import {
  AFTER_SAVE_KEYS,
  PAID_BY_PERSON,
  errorText,
  incomeExpenseSchema,
  multiAccountSchema,
  paidForOthersSchema,
  transactableAccounts,
  useCreateTransaction,
  useEarmarkWarning,
  type IncomeExpenseForm,
} from './formLogic'

export type ExpenseIncomeKind = Extract<AddKind, 'spent' | 'split' | 'paidForOthers' | 'received'>

export function ExpenseIncomeForm({
  kind,
  accounts,
  categories,
  initialSplitContactId,
  initialIncludeYou = true,
  initialPaidByContactId,
  initialAccountId,
  prefill,
  onBack,
  onClose,
}: {
  kind: ExpenseIncomeKind
  accounts: AccountSummary[]
  categories: Category[]
  initialSplitContactId?: string
  initialIncludeYou?: boolean
  initialPaidByContactId?: string
  /** Opened from an account's page: start with that account chosen. */
  initialAccountId?: string
  /** Duplicate: start from this row's values, dated today. */
  prefill?: TransactionSummary
  onBack?: () => void
  onClose: () => void
}) {
  const formId = useId()
  const info = kindInfo(kind)
  const { format } = useFormatCurrency()
  const queryClient = useQueryClient()
  const warnEarmark = useEarmarkWarning()
  const txType = kind === 'received' ? 'INCOME' : 'EXPENSE'
  const isExpense = txType === 'EXPENSE'

  const prefillPaidBy = prefill && !prefill.financialAccountId ? prefill.contactId : undefined
  const [split, setSplit] = useState<SplitValue>(() => ({
    rows: prefill?.splits?.length
      ? prefill.splits.map((s) => ({
          contactId: s.contactId,
          amountPaise: toPaise(s.amount),
          edited: true,
        }))
      : initialSplitContactId
        ? [{ contactId: initialSplitContactId, amountPaise: 0, edited: false }]
        : [],
    includeYou: kind === 'paidForOthers' ? false : initialIncludeYou,
  }))
  const [splitSubmitError, setSplitSubmitError] = useState<string | null>(null)
  const [paidByContactId, setPaidByContactId] = useState(
    initialPaidByContactId ?? prefillPaidBy ?? ''
  )
  const [paidByError, setPaidByError] = useState<string | null>(null)
  const [compensationLink, setCompensationLink] = useState<LinkedTransactionOption | null>(null)
  const [parts, setParts] = useState<PartRow[] | null>(() =>
    prefill?.payments && prefill.payments.length >= 2
      ? prefill.payments.map((p) => ({
          accountId: p.financialAccountId,
          amountPaise: toPaise(p.amount),
          edited: true,
        }))
      : null
  )
  const [partsSubmitError, setPartsSubmitError] = useState<string | null>(null)
  const multiAccount = isExpense && parts !== null
  // "I paid for them, not for me" with someone on the split: unless "Someone else paid" is
  // chosen, this is lending (POST /owed/paid-for-others), not an expense.
  const notPartOfIt =
    SHARED_EXPENSES_ENABLED && isExpense && !split.includeYou && split.rows.length > 0

  const {
    register,
    handleSubmit,
    control,
    setValue,
    setError,
    getValues,
    formState: { errors },
  } = useForm<IncomeExpenseForm>({
    resolver: (values, context, options) =>
      (
        zodResolver(
          notPartOfIt && values.financialAccountId !== PAID_BY_PERSON
            ? paidForOthersSchema
            : multiAccount
              ? multiAccountSchema
              : incomeExpenseSchema
        ) as Resolver<IncomeExpenseForm>
      )(values, context, options),
    defaultValues: prefill
      ? {
          date: today(),
          amount: prefill.amount,
          description: prefill.description,
          financialAccountId: prefill.financialAccountId ?? (prefillPaidBy ? PAID_BY_PERSON : ''),
          categoryId: prefill.categoryId ?? '',
          notes: prefill.notes ?? '',
          attributedToHouseholdMemberId: prefill.attributedToHouseholdMemberId ?? '',
          isCapitalEvent: prefill.isCapitalEvent,
          capitalEventLabel: prefill.capitalEventLabel ?? '',
        }
      : {
          date: today(),
          isCapitalEvent: false,
          categoryId: '',
          financialAccountId: initialPaidByContactId ? PAID_BY_PERSON : (initialAccountId ?? ''),
        },
  })

  // Category is mandatory, so default it to the household's "Other" for this type rather than
  // leaving it blank; a pick that's already valid for the type (e.g. from a suggestion) stays.
  useEffect(() => {
    const current = categories.find((c) => c.id === getValues('categoryId'))
    if (current && current.type === txType) return
    const other = categories.find((c) => c.type === txType && c.name === 'Other')
    if (other) setValue('categoryId', other.id)
  }, [txType, categories, getValues, setValue])

  const watchedAccountId = useWatch({ control, name: 'financialAccountId' })
  const paidByPerson = SHARED_EXPENSES_ENABLED && isExpense && watchedAccountId === PAID_BY_PERSON
  const paidForOthers = notPartOfIt && !paidByPerson
  const isCapitalEvent = useWatch({ control, name: 'isCapitalEvent' })
  const watchedDate = useWatch({ control, name: 'date' })
  const watchedCategoryId = useWatch({ control, name: 'categoryId' })
  const [spendMonth, setSpendMonth] = useState(() => monthOf(today()))
  const [spendMonthPicked, setSpendMonthPicked] = useState(false)
  const { data: spendSuggestion } = useQuery({
    queryKey: ['spend-month-suggestion', watchedDate, watchedCategoryId, watchedAccountId],
    queryFn: () =>
      fetchSpendMonthSuggestion({
        date: watchedDate,
        categoryId: watchedCategoryId || undefined,
        financialAccountId: watchedAccountId || undefined,
      }),
    enabled: !isExpense && !!watchedDate,
    staleTime: Infinity,
  })
  // Learned from the last income like this one, until the user picks a month themselves.
  const suggested = spendSuggestion?.spendMonth
  const suggestionFits =
    !!watchedDate &&
    (suggested === monthOf(watchedDate) || suggested === addMonths(monthOf(watchedDate), 1))
  const shownSpendMonth = !spendMonthPicked && suggestionFits && suggested ? suggested : spendMonth
  const [repeats, setRepeats] = useState<RepeatsValue>(REPEATS_OFF)
  const [repeatsSubmitError, setRepeatsSubmitError] = useState<string | null>(null)
  // A one-off is by definition not recurring, and "paid for others" books as money owed, not spend.
  const canRepeat = !paidForOthers && !isCapitalEvent && !multiAccount
  // Derived, not reset: a hidden Repeats is simply off, and is never sent.
  const repeatsOn = canRepeat && repeats.on
  const { data: contacts = [] } = useQuery({
    queryKey: ['contacts', { includeArchived: false }],
    queryFn: () => listContacts(false),
    enabled: SHARED_EXPENSES_ENABLED && isExpense,
  })

  const [showSuggestions, setShowSuggestions] = useState(false)
  const descriptionValue = useWatch({ control, name: 'description' })
  const watchedAmount = useWatch({ control, name: 'amount' })
  const debouncedDescription = useDebouncedValue(descriptionValue ?? '', 250)
  const { data: suggestions } = useQuery({
    queryKey: ['transaction-suggestions', debouncedDescription],
    queryFn: () => suggestTransactions(debouncedDescription),
    enabled: showSuggestions && debouncedDescription.trim().length >= 2,
    staleTime: 60_000,
  })
  // Only entries of this form's own type can be recreated here.
  const usableSuggestions = (suggestions ?? []).filter((s) => s.type === txType)

  const { data: membersData } = useQuery({ queryKey: ['members'], queryFn: () => listMembers() })
  const memberOptions = toMemberOptions(membersData)

  function applySuggestion(s: TransactionSuggestion) {
    setValue('description', s.description)
    // An expense someone else paid has no account — keep whatever account is already chosen.
    if (s.financialAccountId) setValue('financialAccountId', s.financialAccountId)
    setValue('amount', s.amount)
    setValue('categoryId', s.categoryId ?? '')
    setValue('notes', s.notes ?? '')
    setShowSuggestions(false)
  }

  const create = useCreateTransaction(onClose)
  const paidForOthersMutation = useMutation({
    mutationFn: createPaidForOthers,
    onSuccess: (data) => {
      for (const key of AFTER_SAVE_KEYS) queryClient.invalidateQueries({ queryKey: [key] })
      warnEarmark(data?.earmarkWarning)
      onClose()
    },
  })

  const repeatMutation = useMutation({
    mutationFn: createRecurringFromTransaction,
    onSuccess: (data, payload) => {
      track('transaction_added', { type: payload.transaction.type })
      for (const key of [...AFTER_SAVE_KEYS, 'recurring']) {
        queryClient.invalidateQueries({ queryKey: [key] })
      }
      warnEarmark(data?.earmarkWarning)
      onClose()
    },
  })

  function onSubmit(values: IncomeExpenseForm) {
    if (multiAccount && parts) {
      const err = partsError(toPaise(values.amount), parts, format)
      setPartsSubmitError(err)
      if (err) return
    }
    if (paidByPerson) {
      if (!paidByContactId) {
        setPaidByError('Choose who paid')
        return
      }
    } else if (values.financialAccountId === PAID_BY_PERSON) {
      setError('financialAccountId', { message: 'Account is required' })
      return
    }
    const splitting = !multiAccount && !paidByPerson && isExpense && split.rows.length > 0
    if (splitting) {
      const totalPaise = toPaise(values.amount)
      const sharesPaise = split.rows.map((r) => r.amountPaise)
      let err = splitError(totalPaise, sharesPaise)
      if (!err && paidForOthers && yourShare(totalPaise, sharesPaise) !== 0) {
        err = 'The shares must add up to the full amount'
      }
      setSplitSubmitError(err)
      if (err) return
    }
    if (paidForOthers && !multiAccount) {
      paidForOthersMutation.mutate({
        financialAccountId: values.financialAccountId,
        date: values.date,
        description: values.description,
        shares: splitPayload(split),
        notes: values.notes || undefined,
      })
      return
    }
    const payload: CreateTransactionPayload = {
      type: txType,
      financialAccountId: paidByPerson || multiAccount ? undefined : values.financialAccountId,
      paidByContactId: paidByPerson ? paidByContactId : undefined,
      payments: multiAccount && parts ? toPayload(parts) : undefined,
      amount: values.amount,
      date: values.date,
      description: values.description,
      categoryId: values.categoryId || undefined,
      notes: values.notes || undefined,
      attributedToHouseholdMemberId: values.attributedToHouseholdMemberId || undefined,
      isCapitalEvent: values.isCapitalEvent,
      capitalEventLabel: values.isCapitalEvent ? values.capitalEventLabel || undefined : undefined,
      // Seller compensation link — INCOME only, uncapped, purely for display context.
      relatedTransactionId: isExpense ? undefined : (compensationLink?.id ?? undefined),
      splits: splitting ? splitPayload(split) : undefined,
      spendMonth: isExpense ? undefined : shownSpendMonth,
    }
    if (repeatsOn) {
      const err = repeatsError(repeats, values.date)
      setRepeatsSubmitError(err)
      if (err) return
      repeatMutation.mutate({
        transaction: payload,
        frequency: repeats.frequency,
        kind: isExpense ? repeats.kind : undefined,
        nextDueDate: effectiveNextDue(repeats, values.date),
      })
      return
    }
    create.mutate(payload)
  }

  const failed = create.error ?? paidForOthersMutation.error ?? repeatMutation.error
  const moreLabel = isExpense
    ? 'More — member, note, one-off'
    : 'More — member, note, one-off, link to a purchase'

  return (
    <FormShell
      title={info.title ?? info.label}
      Icon={info.Icon}
      tone={info.tone}
      onBack={onBack}
      onClose={onClose}
      formId={formId}
      submitLabel="Save"
      submitting={create.isPending || paidForOthersMutation.isPending || repeatMutation.isPending}
      error={failed ? errorText(failed) : null}
    >
      <form id={formId} onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
        {prefill && (
          <InfoNote>
            Copy of “{prefill.description}” from {formatDate(prefill.date)}. The date is set to
            today.
          </InfoNote>
        )}
        <AmountHero error={errors.amount?.message}>
          <Controller
            control={control}
            name="amount"
            render={({ field }) => (
              <AmountCalculatorInput
                value={field.value?.toString() ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                placeholder="0.00"
              />
            )}
          />
        </AmountHero>

        <Field label="Description" error={errors.description?.message}>
          <div className="relative">
            <Input
              {...register('description')}
              placeholder="e.g. Grocery shopping"
              autoComplete="off"
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
            />
            {showSuggestions && usableSuggestions.length > 0 && (
              <ul
                aria-label="Fill from last time"
                className={`${MENU_SURFACE} absolute z-10 mt-1 w-full max-h-56 overflow-y-auto py-1`}
              >
                <li className="px-3 pt-1 pb-0.5 text-[11px] font-semibold uppercase tracking-wider text-[var(--fl-text-3)]">
                  Fill from last time
                </li>
                {usableSuggestions.map((s) => (
                  <li key={s.description}>
                    <Button
                      type="button"
                      variant="ghost"
                      onMouseDown={() => applySuggestion(s)}
                      className="h-auto w-full items-center justify-between gap-2 rounded-none px-3 py-2 text-left text-sm font-normal hover:bg-[var(--fl-surface-hover)]"
                    >
                      <span className="font-medium text-[var(--fl-text-1)]">{s.description}</span>
                      <span className="text-xs text-[var(--fl-text-2)]">{format(s.amount)}</span>
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Field>

        <Field
          label={isExpense ? 'Paid from' : 'Received into'}
          error={multiAccount ? undefined : errors.financialAccountId?.message}
        >
          {multiAccount && parts ? (
            <div>
              <PaymentSourcesSection
                totalRupees={Number(watchedAmount) || 0}
                accounts={accounts}
                value={parts}
                onChange={(rows) => {
                  setPartsSubmitError(null)
                  setParts(rows)
                }}
              />
              {partsSubmitError && (
                <p className="mt-1 text-xs text-[var(--fl-error)]">{partsSubmitError}</p>
              )}
              <Button
                type="button"
                variant="ghost"
                onClick={() => setParts(null)}
                className="mt-1 h-auto px-1 py-1 text-sm text-[var(--fl-primary-text)]"
              >
                Use one account
              </Button>
            </div>
          ) : (
            <Controller
              control={control}
              name="financialAccountId"
              render={({ field }) => (
                <Select
                  aria-label={isExpense ? 'Paid from' : 'Received into'}
                  value={field.value ?? ''}
                  onChange={(v) => {
                    setPaidByError(null)
                    field.onChange(v)
                  }}
                  onBlur={field.onBlur}
                >
                  <option value="">Select account</option>
                  {transactableAccounts(accounts).map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                  {SHARED_EXPENSES_ENABLED && isExpense && (
                    <option value={PAID_BY_PERSON}>Someone else paid</option>
                  )}
                </Select>
              )}
            />
          )}
          {!multiAccount && paidByPerson && (
            <div className="mt-2">
              <Select
                aria-label="Who paid"
                value={paidByContactId}
                onChange={(v) => {
                  setPaidByError(null)
                  setPaidByContactId(v)
                }}
              >
                <option value="">Select person</option>
                {contacts.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
              <AddPersonInline
                onAdded={(c) => {
                  setPaidByError(null)
                  setPaidByContactId(c.id)
                }}
              />
              {paidByError && <p className="mt-1 text-xs text-[var(--fl-error)]">{paidByError}</p>}
            </div>
          )}
          {isExpense &&
            !multiAccount &&
            kind !== 'paidForOthers' &&
            kind !== 'split' &&
            !paidByPerson &&
            !notPartOfIt &&
            split.rows.length === 0 && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  const total = toPaise(Number(watchedAmount) || 0)
                  setPartsSubmitError(null)
                  setParts(
                    fillRemainder(total, [
                      { accountId: watchedAccountId || '', amountPaise: total, edited: false },
                      { accountId: '', amountPaise: 0, edited: false },
                    ])
                  )
                }}
                className="mt-1 h-auto px-1 py-1 text-sm text-[var(--fl-primary-text)]"
              >
                Paid from more than one account
              </Button>
            )}
        </Field>

        {!paidForOthers && (
          <Field label="Category" error={errors.categoryId?.message}>
            <Controller
              control={control}
              name="categoryId"
              render={({ field }) => (
                <CategoryListbox
                  categories={categories}
                  type={txType}
                  value={field.value ?? ''}
                  onChange={field.onChange}
                />
              )}
            />
          </Field>
        )}

        <Field label="Date" error={errors.date?.message}>
          <Controller
            control={control}
            name="date"
            render={({ field }) => <DatePicker value={field.value} onChange={field.onChange} />}
          />
        </Field>

        {!isExpense && (
          <SpendMonthField
            date={watchedDate}
            value={shownSpendMonth}
            onChange={(month, byUser) => {
              setSpendMonth(month)
              if (byUser) setSpendMonthPicked(true)
            }}
          />
        )}

        {SHARED_EXPENSES_ENABLED && isExpense && !paidByPerson && !multiAccount && (
          <div>
            <SplitSection
              totalRupees={Number(watchedAmount) || 0}
              value={split}
              defaultOpen={kind === 'split' || kind === 'paidForOthers'}
              onChange={(v) => {
                setSplitSubmitError(null)
                setSplit(v)
              }}
              excludeYouLabel="I paid for them, not for me"
            />
            {splitSubmitError && (
              <p className="mt-1 text-xs text-[var(--fl-error)]">{splitSubmitError}</p>
            )}
          </div>
        )}

        {canRepeat && (
          <div>
            <RepeatsSection
              value={repeats}
              onChange={(v) => {
                setRepeatsSubmitError(null)
                setRepeats(v)
              }}
              date={watchedDate ?? today()}
              isExpense={isExpense}
            />
            {repeatsSubmitError && (
              <p className="mt-1 text-xs text-[var(--fl-error)]">{repeatsSubmitError}</p>
            )}
          </div>
        )}

        <MoreSection
          label={moreLabel}
          defaultOpen={
            !!(prefill?.notes || prefill?.attributedToHouseholdMemberId || prefill?.isCapitalEvent)
          }
        >
          {!paidForOthers && (
            <Field label="Attributed To (optional)">
              <Controller
                control={control}
                name="attributedToHouseholdMemberId"
                render={({ field }) => (
                  <Select
                    aria-label="Attributed To"
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  >
                    <option value="">None</option>
                    {memberOptions.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.label}
                      </option>
                    ))}
                  </Select>
                )}
              />
            </Field>
          )}
          <Field label="Notes (optional)">
            <Textarea {...register('notes')} rows={2} />
          </Field>
          {!paidForOthers && !repeatsOn && (
            <label className="flex items-center gap-2 text-sm text-[var(--fl-text-1)]">
              <Checkbox {...register('isCapitalEvent')} />
              One-off (capital) event — kept out of monthly totals
            </label>
          )}
          {isCapitalEvent && !paidForOthers && (
            <Field label="Event label (optional)">
              <Input {...register('capitalEventLabel')} placeholder="e.g. Annual bonus" />
            </Field>
          )}
          {!isExpense && (
            <Field label="Link to a purchase (optional)">
              <TransactionLinkCombobox
                value={compensationLink}
                label="linked transaction"
                placeholder="e.g. the expense this compensates for…"
                onChange={setCompensationLink}
              />
              <p className="mt-1 flex items-center gap-1 text-xs text-[var(--fl-text-3)]">
                <Link2 size={11} aria-hidden="true" /> For seller compensation on a purchase
              </p>
            </Field>
          )}
        </MoreSection>
      </form>
    </FormShell>
  )
}
